// Modal overlay shown while an audio/video export is being prepared.
//
// Behaviour:
//   - Visible while `busy` is true.
//   - Once `busy` flips back to false, the modal stays visible until at
//     least MIN_VISIBLE_MS have elapsed since it appeared. Without this
//     a fast voice-only WAV export would flash the modal for ~150ms,
//     which feels glitchy. The 1s floor is long enough to read the
//     "Preparing…" text and confirm something happened.
//   - No close button, no Escape handler, no backdrop click. The user
//     waits for the export to finish — same UX promise as a print
//     dialog. Critical for long video exports where an accidental
//     dismiss would discard the half-rendered output.
//
// `status` (the live phase string from useExport — "Rendering audio…",
// "Encoding video…", "Saving…") is shown as a secondary line so the
// user has a sense of progress on the long jobs. Empty status falls
// through to a non-breaking space so the panel height doesn't jump
// when status arrives mid-export.

import { useEffect, useRef, useState } from 'react';
import styles from './ExportProgressModal.module.css';

interface Props {
  busy: boolean;
  status: string;
}

const MIN_VISIBLE_MS = 1000;

export function ExportProgressModal({ busy, status }: Props) {
  const [show, setShow] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (busy) {
      // Open immediately, mark the wall-clock time so we can floor the
      // visible duration later. Re-marking on a re-entry (busy=false→true
      // before MIN_VISIBLE_MS elapsed) is fine — we want the floor to
      // measure from the LATEST open.
      setShow(true);
      shownAtRef.current = performance.now();
      return;
    }
    if (!show) return;
    const elapsed = performance.now() - (shownAtRef.current ?? 0);
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    if (remaining === 0) {
      setShow(false);
      return;
    }
    const t = window.setTimeout(() => setShow(false), remaining);
    return () => window.clearTimeout(t);
  }, [busy, show]);

  if (!show) return null;

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.panel}>
        <i className={`ph-bold ph-file-arrow-down ${styles.spinner}`} aria-hidden="true" />
        <div className={styles.title}>Preparing export, please wait...</div>
        <div className={styles.status}>{status || ' '}</div>
      </div>
    </div>
  );
}
